"use client";

import * as React from "react";
import { LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// StatDisplay - Display statistics with optional trends
// ============================================================================

export interface StatDisplayProps {
  /** Label for the stat */
  label: string;
  /** The main value to display */
  value: string | number;
  /** Optional subtext below the value */
  subtext?: string;
  /** Icon component */
  icon?: LucideIcon;
  /** Icon color variant */
  iconVariant?: "default" | "emerald" | "blue" | "amber" | "purple" | "rose";
  /** Trend direction */
  trend?: "up" | "down" | "neutral";
  /** Trend value (e.g., "+12%") */
  trendValue?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Additional className */
  className?: string;
}

const iconVariants = {
  default: "text-muted-foreground",
  emerald: "text-emerald-600 dark:text-emerald-400",
  blue: "text-blue-600 dark:text-blue-400",
  amber: "text-amber-600 dark:text-amber-400",
  purple: "text-purple-600 dark:text-purple-400",
  rose: "text-rose-600 dark:text-rose-400",
};

const sizeStyles = {
  sm: {
    value: "text-xl font-bold",
    label: "text-xs",
    subtext: "text-[10px]",
    icon: "h-4 w-4",
  },
  md: {
    value: "text-2xl font-bold",
    label: "text-sm",
    subtext: "text-xs",
    icon: "h-5 w-5",
  },
  lg: {
    value: "text-3xl font-bold",
    label: "text-base",
    subtext: "text-sm",
    icon: "h-6 w-6",
  },
};

export function StatDisplay({
  label,
  value,
  subtext,
  icon: Icon,
  iconVariant = "default",
  trend,
  trendValue,
  size = "md",
  className,
}: StatDisplayProps) {
  const styles = sizeStyles[size];
  
  const TrendIcon = trend === "up" 
    ? TrendingUp 
    : trend === "down" 
    ? TrendingDown 
    : Minus;
  
  const trendColor = trend === "up" 
    ? "text-emerald-600" 
    : trend === "down" 
    ? "text-rose-600" 
    : "text-muted-foreground";

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-center gap-2">
        {Icon && (
          <Icon className={cn(styles.icon, iconVariants[iconVariant])} />
        )}
        <span className={cn(styles.label, "font-medium text-muted-foreground")}>
          {label}
        </span>
      </div>
      
      <div className="flex items-baseline gap-2">
        <span className={styles.value}>{value}</span>
        {trend && trendValue && (
          <span className={cn("flex items-center gap-0.5 text-xs font-medium", trendColor)}>
            <TrendIcon className="h-3 w-3" />
            {trendValue}
          </span>
        )}
      </div>
      
      {subtext && (
        <p className={cn(styles.subtext, "text-muted-foreground")}>
          {subtext}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// StatCard - Stat display in a card container
// ============================================================================

export interface StatCardProps extends StatDisplayProps {
  /** Background variant */
  bgVariant?: "default" | "emerald" | "blue" | "amber" | "purple" | "rose";
}

const bgVariants = {
  default: "bg-card",
  emerald: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30",
  blue: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
  amber: "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30",
  purple: "bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30",
  rose: "bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30",
};

export function StatCard({
  bgVariant = "default",
  className,
  ...props
}: StatCardProps) {
  return (
    <div className={cn(
      "rounded-xl border p-4",
      bgVariants[bgVariant],
      className
    )}>
      <StatDisplay {...props} />
    </div>
  );
}

// ============================================================================
// StatGrid - Grid container for stat displays
// ============================================================================

export interface StatGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function StatGrid({ 
  children, 
  columns = 4,
  className 
}: StatGridProps) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4",
  };

  return (
    <div className={cn("grid gap-4", gridCols[columns], className)}>
      {children}
    </div>
  );
}

// ============================================================================
// MiniStat - Compact inline stat
// ============================================================================

export interface MiniStatProps {
  label: string;
  value: string | number;
  colorVariant?: "default" | "emerald" | "blue" | "amber" | "purple" | "rose";
  className?: string;
}

const miniStatColors = {
  default: "text-foreground",
  emerald: "text-emerald-600 dark:text-emerald-400",
  blue: "text-blue-600 dark:text-blue-400",
  amber: "text-amber-600 dark:text-amber-400",
  purple: "text-purple-600 dark:text-purple-400",
  rose: "text-rose-600 dark:text-rose-400",
};

export function MiniStat({
  label,
  value,
  colorVariant = "default",
  className,
}: MiniStatProps) {
  return (
    <div className={cn("text-center", className)}>
      <div className={cn("text-2xl font-bold", miniStatColors[colorVariant])}>
        {value}
      </div>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

// ============================================================================
// SimpleStatGrid - Simple grid for stat cells (legacy compatibility)
// ============================================================================

export type GridColumns = 2 | 3 | 4;
export type GridGap = "sm" | "md" | "lg";
export type CellVariant = "default" | "muted" | "transparent";

export interface SimpleStatGridProps {
  columns?: GridColumns;
  gap?: GridGap;
  className?: string;
  children: React.ReactNode;
}

const columnClasses: Record<GridColumns, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const gapClasses: Record<GridGap, string> = {
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
};

export function SimpleStatGrid({
  columns = 2,
  gap = "md",
  className,
  children,
}: SimpleStatGridProps) {
  return (
    <div className={cn("grid", columnClasses[columns], gapClasses[gap], className)}>
      {children}
    </div>
  );
}

// ============================================================================
// StatCell - Individual stat cell for use in grids
// ============================================================================

export interface StatCellProps {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  variant?: CellVariant;
  size?: "sm" | "md";
  className?: string;
}

const variantClasses: Record<CellVariant, string> = {
  default: "bg-muted/50",
  muted: "bg-muted/30",
  transparent: "bg-transparent",
};

export function StatCell({
  icon: Icon,
  label,
  value,
  variant = "default",
  size = "md",
  className,
}: StatCellProps) {
  const isSmall = size === "sm";

  return (
    <div
      className={cn(
        "rounded-lg text-center",
        variantClasses[variant],
        isSmall ? "p-2" : "p-3",
        className
      )}
    >
      {Icon && (
        <div className="flex items-center justify-center gap-1 mb-1">
          <Icon
            className={cn(
              "text-muted-foreground",
              isSmall ? "h-3 w-3" : "h-4 w-4"
            )}
          />
        </div>
      )}
      <p
        className={cn(
          "text-muted-foreground mb-1",
          isSmall ? "text-[10px]" : "text-xs"
        )}
      >
        {label}
      </p>
      <p className={cn("font-bold", isSmall ? "text-sm" : "text-lg")}>
        {value}
      </p>
    </div>
  );
}
